/**
 * Per-token reveal state machine — spec §7.3 / §8.1.
 *
 * Single shielded `eth_call` with one envelope signature. No second EIP-712
 * digest, no signature cache. The decrypted balance value is cached in
 * localStorage for ~5 min purely as a UX optimization (no re-prompt for a
 * quick glance) — every fresh refresh re-signs the envelope.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import useWallet from '@/hooks/wallets/useWallet'
import useIsSafeOwner from '@/hooks/useIsSafeOwner'
import useSafeAddress from '@/hooks/useSafeAddress'
import { useCurrentChain } from '@/hooks/useChains'
import { getRpcServiceUrl } from '@/hooks/wallets/web3'
import { logError, Errors } from '@/services/exceptions'
import { asError } from '@safe-global/utils/services/exceptions/utils'
import { useSrc20Config } from './useSrc20Config'
import { useIsSrc20ModuleEnabled } from './useIsSrc20ModuleEnabled'
import { useBalanceValueCache } from './useBalanceValueCache'
import { readShieldedBalance, mapReaderError } from '../services/readBalance'
import type { Src20BalanceState, Src20TokenInfo } from '../types'

export interface UseSrc20BalanceResult {
  state: Src20BalanceState
  /** Trigger a reveal. No-op if the wallet is not an owner or module not enabled. */
  reveal: (opts?: { force?: boolean }) => Promise<void>
  /** Reset to the initial 'hidden' state. */
  reset: () => void
}

export const useSrc20Balance = (token: Src20TokenInfo | undefined): UseSrc20BalanceResult => {
  const cfg = useSrc20Config()
  const safeAddress = useSafeAddress()
  const chain = useCurrentChain()
  const wallet = useWallet()
  const isOwner = useIsSafeOwner()
  const { enabled: moduleEnabled } = useIsSrc20ModuleEnabled()
  const balanceCache = useBalanceValueCache()

  const [state, setState] = useState<Src20BalanceState>({ status: 'hidden' })

  // First-paint from cache + reset on key parts changing.
  useEffect(() => {
    if (!cfg || !token || !safeAddress) {
      setState({ status: 'hidden' })
      return
    }
    if (moduleEnabled === false) {
      setState({ status: 'moduleNotEnabled' })
      return
    }
    const signer = wallet?.address as Address | undefined
    if (!signer) {
      setState({ status: 'hidden' })
      return
    }
    const cached = balanceCache.get({
      chainId: cfg.chainId,
      safe: safeAddress as Address,
      token: token.address,
      signer,
    })
    if (cached) {
      setState({
        status: 'revealed',
        raw: cached.raw,
        formatted: formatUnits(cached.raw, token.decimals),
        revealedAt: cached.revealedAt,
        signer,
        fromCache: true,
      })
    } else {
      setState({ status: 'hidden' })
    }
  }, [cfg, token, safeAddress, wallet?.address, moduleEnabled, balanceCache])

  const reveal = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!cfg || !token || !safeAddress || !wallet || !chain) return
      if (!isOwner) return
      if (moduleEnabled === false) {
        setState({ status: 'moduleNotEnabled' })
        return
      }
      const signer = wallet.address as Address

      if (!force) {
        const cached = balanceCache.get({
          chainId: cfg.chainId,
          safe: safeAddress as Address,
          token: token.address,
          signer,
        })
        if (cached) {
          setState({
            status: 'revealed',
            raw: cached.raw,
            formatted: formatUnits(cached.raw, token.decimals),
            revealedAt: cached.revealedAt,
            signer,
            fromCache: true,
          })
          return
        }
      }

      setState({ status: 'signing' })
      try {
        // Apply the Infura-token substitution (or any other auth in the rpcUri)
        // so a chain whose RPC needs a key actually reaches a working endpoint
        // instead of failing with a generic JSON-RPC object.
        const rpcUrl = chain.rpcUri ? getRpcServiceUrl(chain.rpcUri) : ''
        if (!rpcUrl) throw new Error('Chain RPC URL is not configured')

        // The signing prompt happens inside `signedShieldedRead` after the
        // calldata has been encrypted; we stay in 'signing' until then,
        // then flip to 'reading' once the user accepts and the eth_call goes out.
        setState({ status: 'reading' })
        const raw = await readShieldedBalance({
          chainId: cfg.chainId,
          rpcUrl,
          provider: wallet.provider,
          signer,
          reader: cfg.readerAddress,
          safe: safeAddress as Address,
          token: token.address,
        })

        balanceCache.set({
          chainId: cfg.chainId,
          safe: safeAddress as Address,
          token: token.address,
          signer,
          raw,
        })

        setState({
          status: 'revealed',
          raw,
          formatted: formatUnits(raw, token.decimals),
          revealedAt: Math.floor(Date.now() / 1000),
          signer,
          fromCache: false,
        })
      } catch (err) {
        const mapped = mapReaderError(err)
        // Wrap whatever was thrown into a real Error before logging so the
        // log line reads "(message)" rather than "(Non-Error object of type:
        // object)" when the wallet/JSON-RPC throws a plain `{code,message}`.
        const normalised = asError(err)
        if (mapped.kind === 'moduleCallFailed') {
          logError(Errors._609, normalised)
        } else if (mapped.kind !== 'userRejected') {
          logError(Errors._601, normalised)
        }
        setState({ status: 'error', error: mapped })
      }
    },
    [cfg, token, safeAddress, wallet, chain, isOwner, moduleEnabled, balanceCache],
  )

  const reset = useCallback(() => setState({ status: 'hidden' }), [])

  return useMemo(() => ({ state, reveal, reset }), [state, reveal, reset])
}

export default useSrc20Balance
